import { getAgentAvatarUrl } from "../lib/agentAvatars.js";

interface TypingIndicatorProps {
  label: string;
  avatarName?: string | null;
  size?: "md" | "sm";
  className?: string;
}

/**
 * Animated "is typing" affordance. Warm pill with halo pulse, drifting
 * vertical float, and a three-dot wave. Used in the main channel and
 * inside ThreadPanel's InlineChat.
 */
export function TypingIndicator({
  label,
  avatarName,
  size = "md",
  className,
}: TypingIndicatorProps) {
  const isSmall = size === "sm";
  const avatar = avatarName ? getAgentAvatarUrl(avatarName) : null;
  const dotSize = isSmall ? 5 : 6;
  const padX = isSmall ? "px-2.5" : "px-3";
  const padY = isSmall ? "py-1.5" : "py-2";
  const gap = isSmall ? "gap-2" : "gap-2.5";
  const textSize = isSmall ? "text-[11px]" : "text-[12px]";
  const avatarSize = isSmall ? 18 : 22;

  return (
    <div
      aria-live="polite"
      className={`typing-indicator-shell inline-flex items-center ${gap} ${padX} ${padY} rounded-full ${className ?? ""}`}
      style={{
        background:
          "linear-gradient(135deg, var(--print-bg-solid), var(--field-base))",
        border: "1px solid var(--print-border)",
        boxShadow: "var(--shadow-print)",
        color: "var(--text-secondary)",
      }}
    >
      {avatar ? (
        <span
          className="typing-indicator-halo rounded-md overflow-hidden shrink-0"
          style={{ width: avatarSize, height: avatarSize }}
        >
          <img
            src={avatar}
            alt=""
            className="w-full h-full object-cover"
            aria-hidden="true"
          />
        </span>
      ) : (
        <span
          className="typing-indicator-halo rounded-full shrink-0"
          style={{
            width: avatarSize - 8,
            height: avatarSize - 8,
            background: "var(--accent)",
          }}
          aria-hidden="true"
        />
      )}
      <span className={`${textSize} font-medium tracking-tight`}>{label}</span>
      <span
        className="flex items-end gap-[3px]"
        aria-hidden="true"
        style={{ height: dotSize + 4 }}
      >
        <span
          className="typing-indicator-dot rounded-full"
          style={{
            width: dotSize,
            height: dotSize,
            background: "var(--accent)",
            animationDelay: "0s",
          }}
        />
        <span
          className="typing-indicator-dot rounded-full"
          style={{
            width: dotSize,
            height: dotSize,
            background: "var(--accent)",
            animationDelay: "0.15s",
          }}
        />
        <span
          className="typing-indicator-dot rounded-full"
          style={{
            width: dotSize,
            height: dotSize,
            background: "var(--accent)",
            animationDelay: "0.3s",
          }}
        />
      </span>
    </div>
  );
}
